import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, Check, AlertCircle, Edit, ChevronDown, Users, CheckCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const STATUS_TONE = {
  logged: "text-slate-700 border-slate-200 bg-slate-50",
  submitted: "text-amber-700 border-amber-200 bg-amber-50",
  approved: "text-emerald-700 border-emerald-200 bg-emerald-50",
  rejected: "text-red-700 border-red-200 bg-red-50",
};

function diffHours(timeIn, timeOut, breakMin) {
  if (!timeIn || !timeOut) return 0;
  const [hi, mi] = timeIn.split(":").map(Number);
  const [ho, mo] = timeOut.split(":").map(Number);
  const mins = (ho * 60 + mo) - (hi * 60 + mi) - Number(breakMin || 0);
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

export default function TimeTrackingPage() {
  const { user } = useAuth();
  const isStaff = ["super_admin", "admin", "hr_user", "manager"].includes(user?.role);

  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(() => localStorage.getItem("tt:lastEmp") || "");
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [timeIn, setTimeIn] = useState("09:00");
  const [timeOut, setTimeOut] = useState("17:00");
  const [breakMin, setBreakMin] = useState(30);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState({
    standard: { label: "Usual 9–5:30", time_in: "09:00", time_out: "17:30", break_minutes: 30 },
    early:    { label: "Early",        time_in: "07:00", time_out: "15:00", break_minutes: 30 },
    late:     { label: "Late",         time_in: "13:00", time_out: "21:00", break_minutes: 30 },
  });

  // Existing entry for the selected (employee, date)
  const [existing, setExisting] = useState(null);
  const [existingLoading, setExistingLoading] = useState(false);

  // Today's grid for managers
  const [gridDate, setGridDate] = useState(todayStr());
  const [gridRows, setGridRows] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridSel, setGridSel] = useState(() => new Set());

  // Bulk log dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkDefaults = { date: todayStr(), in: "09:00", out: "17:00", brk: 30, notes: "" };
  const [bulkDate, setBulkDate] = useState(bulkDefaults.date);
  const [bulkIn, setBulkIn] = useState(bulkDefaults.in);
  const [bulkOut, setBulkOut] = useState(bulkDefaults.out);
  const [bulkBreak, setBulkBreak] = useState(bulkDefaults.brk);
  const [bulkNotes, setBulkNotes] = useState(bulkDefaults.notes);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkStatusMap, setBulkStatusMap] = useState({}); // employee_id -> "entry object" | null

  const bulkIsDirty = () => (
    bulkSelected.size > 0 ||
    bulkNotes.trim() !== "" ||
    bulkDate !== bulkDefaults.date ||
    bulkIn !== bulkDefaults.in ||
    bulkOut !== bulkDefaults.out ||
    Number(bulkBreak) !== bulkDefaults.brk
  );
  const resetBulk = () => {
    setBulkDate(bulkDefaults.date); setBulkIn(bulkDefaults.in); setBulkOut(bulkDefaults.out);
    setBulkBreak(bulkDefaults.brk); setBulkNotes(bulkDefaults.notes);
    setBulkSelected(new Set()); setBulkSearch("");
  };
  const requestCloseBulk = () => {
    if (bulkSaving) return; // never interrupt a save in flight
    if (bulkIsDirty()) {
      const ok = window.confirm("You have unsaved changes.\n\n• Click OK to DISCARD changes and close.\n• Click Cancel to go back and save / review.");
      if (!ok) return;
    }
    resetBulk();
    setBulkOpen(false);
  };

  const loadEmployees = async () => {
    try {
      const { data } = await api.get("/employees", { params: { status: "active" } });
      setEmployees(data);
      if (!employeeId && data[0]) setEmployeeId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
    // Shift presets
    try {
      const s = await api.get("/settings");
      if (s.data?.shift_presets) setPresets(s.data.shift_presets);
    } catch { /* ignore */ }
  };

  const loadExisting = async () => {
    if (!employeeId || !date) { setExisting(null); return; }
    setExistingLoading(true);
    try {
      const { data } = await api.get("/time-entries", { params: { employee_id: employeeId, start: date, end: date } });
      setExisting(data?.[0] || null);
    } catch (e) {
      setExisting(null);
    } finally { setExistingLoading(false); }
  };

  const loadGrid = async () => {
    if (!isStaff) return;
    setGridLoading(true);
    try {
      const [empsRes, entriesRes] = await Promise.all([
        api.get("/employees", { params: { status: "active" } }),
        api.get("/time-entries", { params: { start: gridDate, end: gridDate } }),
      ]);
      const byId = {};
      for (const e of entriesRes.data) byId[e.employee_id] = e;
      const rows = empsRes.data.map((emp) => ({ employee: emp, entry: byId[emp.id] || null }));
      // sort: pending/missing first, then by name
      const order = (r) => !r.entry ? 0 : r.entry.status === "submitted" ? 1 : r.entry.status === "approved" ? 3 : 2;
      rows.sort((a, b) => order(a) - order(b) || a.employee.full_name.localeCompare(b.employee.full_name));
      setGridRows(rows);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGridLoading(false); }
  };

  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { loadExisting(); /* eslint-disable-next-line */ }, [employeeId, date]);
  useEffect(() => { if (employeeId) localStorage.setItem("tt:lastEmp", employeeId); }, [employeeId]);
  useEffect(() => { loadGrid(); /* eslint-disable-next-line */ }, [gridDate]);

  const filteredEmps = employees;

  const previewHours = useMemo(() => diffHours(timeIn, timeOut, breakMin), [timeIn, timeOut, breakMin]);

  const presetStandard = () => { const p = presets.standard; setTimeIn(p.time_in); setTimeOut(p.time_out); setBreakMin(Number(p.break_minutes || 0)); };
  const presetEarly    = () => { const p = presets.early;    setTimeIn(p.time_in); setTimeOut(p.time_out); setBreakMin(Number(p.break_minutes || 0)); };
  const presetLate     = () => { const p = presets.late;     setTimeIn(p.time_in); setTimeOut(p.time_out); setBreakMin(Number(p.break_minutes || 0)); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!employeeId) return toast.error("Select an employee");
    if (timeOut <= timeIn) return toast.error("Time out must be after time in");
    if (existing) return toast.error("Entry already exists for this employee + date — edit it instead");
    setSaving(true);
    try {
      await api.post("/time-entries", {
        employee_id: employeeId, date, time_in: timeIn, time_out: timeOut,
        break_minutes: Number(breakMin), notes: notes || undefined,
      });
      toast.success(`Saved · ${previewHours.toFixed(2)}h logged`);
      setNotes("");
      loadExisting();
      if (gridDate === date) loadGrid();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const startEditExisting = () => {
    if (!existing) return;
    setTimeIn(existing.time_in); setTimeOut(existing.time_out);
    setBreakMin(existing.break_minutes); setNotes(existing.notes || "");
  };

  const updateExisting = async () => {
    if (!existing) return;
    setSaving(true);
    try {
      await api.patch(`/time-entries/${existing.id}`, {
        time_in: timeIn, time_out: timeOut, break_minutes: Number(breakMin),
        notes: notes || undefined,
      });
      toast.success("Entry updated");
      loadExisting();
      if (gridDate === date) loadGrid();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const approveEntry = async (entryId) => {
    try {
      await api.patch(`/time-entries/${entryId}`, { status: "approved" });
      toast.success("Approved");
      loadGrid(); loadExisting();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // --- Bulk log dialog ---
  const openBulkDialog = async () => {
    setBulkOpen(true);
    setBulkSelected(new Set());
    setBulkSearch("");
    await refreshBulkStatus(bulkDate);
  };

  const refreshBulkStatus = async (d) => {
    try {
      const { data } = await api.get("/time-entries", { params: { start: d, end: d } });
      const map = {};
      for (const e of data) map[e.employee_id] = e;
      setBulkStatusMap(map);
    } catch { setBulkStatusMap({}); }
  };

  useEffect(() => { if (bulkOpen) refreshBulkStatus(bulkDate); /* eslint-disable-next-line */ }, [bulkDate, bulkOpen]);

  const filteredBulkEmps = useMemo(() => {
    if (!bulkSearch.trim()) return employees;
    const q = bulkSearch.toLowerCase();
    return employees.filter((e) => (e.full_name || "").toLowerCase().includes(q) || (e.employee_id || "").toLowerCase().includes(q) || (e.department || "").toLowerCase().includes(q));
  }, [employees, bulkSearch]);

  const bulkToggle = (id) => {
    setBulkSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const bulkSelectAllNotLogged = () => {
    setBulkSelected(new Set(filteredBulkEmps.filter((e) => !bulkStatusMap[e.id]).map((e) => e.id)));
  };
  const bulkClear = () => setBulkSelected(new Set());
  const bulkSelectAllVisible = () => setBulkSelected(new Set(filteredBulkEmps.map((e) => e.id)));

  const submitBulk = async () => {
    if (bulkSelected.size === 0) return toast.error("Select at least one employee");
    if (bulkOut <= bulkIn) return toast.error("Time out must be after time in");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/time-entries/bulk", {
        date: bulkDate, time_in: bulkIn, time_out: bulkOut,
        break_minutes: Number(bulkBreak), notes: bulkNotes || undefined,
        employee_ids: Array.from(bulkSelected),
      });
      const sk = data.skipped_existing?.length || 0;
      const fl = data.failed?.length || 0;
      toast.success(`${data.created_count} logged · ${data.hours_per_entry}h each${sk ? ` · ${sk} skipped (already logged)` : ""}${fl ? ` · ${fl} failed` : ""}`);
      resetBulk();
      setBulkOpen(false);
      if (gridDate === bulkDate) loadGrid();
      if (date === bulkDate) loadExisting();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkSaving(false); }
  };

  const bulkPresetStandard = () => { const p = presets.standard; setBulkIn(p.time_in); setBulkOut(p.time_out); setBulkBreak(Number(p.break_minutes || 0)); };
  const bulkPresetEarly    = () => { const p = presets.early;    setBulkIn(p.time_in); setBulkOut(p.time_out); setBulkBreak(Number(p.break_minutes || 0)); };
  const bulkPresetLate     = () => { const p = presets.late;     setBulkIn(p.time_in); setBulkOut(p.time_out); setBulkBreak(Number(p.break_minutes || 0)); };
  const bulkPreviewHours = diffHours(bulkIn, bulkOut, bulkBreak);

  // --- Grid row selection + bulk actions from grid ---
  const toggleGridSel = (id) => {
    setGridSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearGridSel = () => setGridSel(new Set());
  const approvableRows = gridRows.filter((r) => r.entry && r.entry.status !== "approved");
  const selectAllApprovable = () => setGridSel(new Set(approvableRows.map((r) => r.entry.id)));

  const bulkApproveSelected = async () => {
    if (gridSel.size === 0) return toast.error("Select at least one entry");
    if (!window.confirm(`Approve ${gridSel.size} entries?`)) return;
    try {
      const { data } = await api.post("/time-entries/bulk-approve", { ids: Array.from(gridSel) });
      toast.success(`${data.approved_count} approved`);
      clearGridSel();
      loadGrid(); loadExisting();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkApproveAllForDay = async () => {
    if (approvableRows.length === 0) return;
    if (!window.confirm(`Approve all ${approvableRows.length} pending entries for ${gridDate}?`)) return;
    try {
      const { data } = await api.post("/time-entries/bulk-approve", { date: gridDate });
      toast.success(`${data.approved_count} approved`);
      loadGrid(); loadExisting();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const quickFillFromGrid = (row) => {
    setEmployeeId(row.employee.id);
    setDate(gridDate);
    if (row.entry) {
      setTimeIn(row.entry.time_in); setTimeOut(row.entry.time_out);
      setBreakMin(row.entry.break_minutes); setNotes(row.entry.notes || "");
    } else {
      presetStandard();
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectedEmp = employees.find((e) => e.id === employeeId);

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Rapid entry</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Log time</h1>
          <p className="text-muted-foreground mt-1">One-click presets, live duplicate detection, and today's status grid below.</p>
        </div>
        {isStaff && (
          <Dialog
            open={bulkOpen}
            onOpenChange={(open) => {
              if (open) { setBulkOpen(true); return; }
              requestCloseBulk();
            }}
          >
            <DialogTrigger asChild>
              <Button variant="outline" className="h-11" onClick={openBulkDialog} data-testid="tt-bulk-open">
                <Users className="w-4 h-4 mr-2" /> Bulk log hours
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-w-3xl"
              data-testid="tt-bulk-dialog"
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => { e.preventDefault(); requestCloseBulk(); }}
            >
              <DialogHeader>
                <DialogTitle>Bulk log hours</DialogTitle>
                <DialogDescription>
                  Log the same shift for many employees at once. Anyone who already has an entry for that day is skipped automatically.
                </DialogDescription>
              </DialogHeader>

              <div className="grid sm:grid-cols-12 gap-3 items-end">
                <div className="sm:col-span-4">
                  <Label>Date</Label>
                  <div className="flex gap-2">
                    <Input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="h-10" data-testid="tt-bulk-date" />
                    <Button type="button" variant={bulkDate === todayStr() ? "default" : "outline"} className="h-10 px-3" onClick={() => setBulkDate(todayStr())}>Today</Button>
                    <Button type="button" variant={bulkDate === yesterdayStr() ? "default" : "outline"} className="h-10 px-3" onClick={() => setBulkDate(yesterdayStr())}>Yesterday</Button>
                  </div>
                </div>
                <div className="sm:col-span-2"><Label>Time in</Label><TimeInput value={bulkIn} onChange={(e) => setBulkIn(e.target.value)} className="h-10" data-testid="tt-bulk-in" /></div>
                <div className="sm:col-span-2"><Label>Time out</Label><TimeInput value={bulkOut} onChange={(e) => setBulkOut(e.target.value)} className="h-10" data-testid="tt-bulk-out" /></div>
                <div className="sm:col-span-2"><Label>Break (min)</Label><Input type="number" min="0" value={bulkBreak} onChange={(e) => setBulkBreak(e.target.value)} className="h-10" data-testid="tt-bulk-break" /></div>
                <div className="sm:col-span-2">
                  <Label>Total</Label>
                  <div className="h-10 px-3 rounded-md border border-input bg-muted/40 flex items-center font-mono font-semibold text-sm">{bulkPreviewHours.toFixed(2)} h</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" size="sm" variant="outline" onClick={bulkPresetStandard}>{presets.standard.label} · {presets.standard.time_in}–{presets.standard.time_out}</Button>
                <Button type="button" size="sm" variant="outline" onClick={bulkPresetEarly}>{presets.early.label} · {presets.early.time_in}–{presets.early.time_out}</Button>
                <Button type="button" size="sm" variant="outline" onClick={bulkPresetLate}>{presets.late.label} · {presets.late.time_in}–{presets.late.time_out}</Button>
              </div>

              <div>
                <Label className="text-xs">Notes (optional — applied to every entry)</Label>
                <Input value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)} placeholder="e.g. Warehouse shift" data-testid="tt-bulk-notes" />
              </div>

              <div className="mt-2">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Employees</Label>
                    <Badge variant="outline" className="text-xs">{bulkSelected.size} selected</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={bulkSelectAllNotLogged} data-testid="tt-bulk-select-not-logged">Select all not yet logged</Button>
                    <Button type="button" size="sm" variant="outline" onClick={bulkSelectAllVisible}>Select all visible</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={bulkClear}>Clear</Button>
                  </div>
                </div>
                <Input value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)} placeholder="Search by name, ID, or department…" className="h-9 mb-2" data-testid="tt-bulk-search" />
                <div className="rounded-md border border-border max-h-72 overflow-y-auto app-scroll">
                  {filteredBulkEmps.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No matches</div>}
                  {filteredBulkEmps.map((e) => {
                    const ent = bulkStatusMap[e.id];
                    const isSel = bulkSelected.has(e.id);
                    const isLocked = !!ent; // already logged — skipping or in-conflict
                    return (
                      <label
                        key={e.id}
                        className={`flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 cursor-pointer hover:bg-muted/40 ${isSel ? "bg-muted/30" : ""}`}
                        data-testid={`tt-bulk-row-${e.employee_id}`}
                      >
                        <Checkbox checked={isSel} onCheckedChange={() => bulkToggle(e.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{e.full_name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {(e.employment_type || "").replace("_", " ")} · {e.contracted_weekly_hours ?? e.standard_weekly_hours ?? "?"}h/wk
                            {e.department ? ` · ${e.department}` : ""}
                          </div>
                        </div>
                        {isLocked ? (
                          <Badge variant="outline" className={STATUS_TONE[ent.status] || ""}>
                            {ent.time_in}–{ent.time_out} · {ent.status}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">not logged</Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Tip: selected employees who already have an entry for {bulkDate} will be <strong>skipped, not overwritten</strong>.
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={requestCloseBulk} data-testid="tt-bulk-cancel">Cancel</Button>
                <Button onClick={submitBulk} disabled={bulkSaving || bulkSelected.size === 0} data-testid="tt-bulk-submit">
                  {bulkSaving ? "Saving…" : `Log ${bulkSelected.size} entries`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* RAPID ENTRY */}
      <Card className="border-border shadow-none">
        <CardContent className="p-6 space-y-5">
          {/* Row 1: employee + date */}
          <div className="grid lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-6">
              <Label>Employee</Label>
              <Popover open={empPickerOpen} onOpenChange={setEmpPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className="w-full h-11 justify-between font-normal"
                    data-testid="tt-employee-trigger"
                  >
                    <span className="truncate text-left">
                      {selectedEmp
                        ? <span className="font-medium">{selectedEmp.full_name}</span>
                        : <span className="text-muted-foreground">Search by name…</span>}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start" data-testid="tt-employee-popover">
                  <Command>
                            <CommandInput placeholder="Type a name…" data-testid="tt-employee-search" />
                    <CommandList>
                      <CommandEmpty>No employees match.</CommandEmpty>
                      <CommandGroup>
                        {filteredEmps.map((e) => (
                          <CommandItem
                            key={e.id}
                            value={`${e.full_name} ${e.employee_id} ${e.email || ""}`}
                            onSelect={() => { setEmployeeId(e.id); setEmpPickerOpen(false); }}
                            data-testid={`tt-emp-${e.employee_id}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{e.full_name}</span>
                              {e.department && (
                                <span className="text-xs text-muted-foreground">{e.department}</span>
                              )}
                            </div>
                            {employeeId === e.id && <Check className="w-4 h-4 ml-auto" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedEmp && (
                <div className="text-xs text-muted-foreground mt-1.5">
                  Contracted: <span className="font-medium text-foreground">{selectedEmp.contracted_weekly_hours ?? selectedEmp.standard_weekly_hours ?? "—"}h/wk</span> · {selectedEmp.working_days_per_week ?? 5} days · <span className="capitalize">{(selectedEmp.employment_type || "").replace("_", " ")}</span>
                </div>
              )}
            </div>
            <div className="lg:col-span-6">
              <Label>Date</Label>
              <div className="flex gap-2">
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" data-testid="tt-date" />
                <Button type="button" variant={date === todayStr() ? "default" : "outline"} className="h-11 px-4" onClick={() => setDate(todayStr())} data-testid="tt-today">Today</Button>
                <Button type="button" variant={date === yesterdayStr() ? "default" : "outline"} className="h-11 px-4" onClick={() => setDate(yesterdayStr())} data-testid="tt-yesterday">Yesterday</Button>
              </div>
            </div>
          </div>

          {/* Status banner — live duplicate detection */}
          <div data-testid="tt-existing-banner">
            {existingLoading ? (
              <div className="text-xs text-muted-foreground">Checking for existing entry…</div>
            ) : existing ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    Already logged for <strong>{selectedEmp?.full_name}</strong> on {date} ·{" "}
                    <strong>{existing.time_in}–{existing.time_out}</strong> · {existing.total_hours.toFixed(2)}h
                  </span>
                  <Badge variant="outline" className={STATUS_TONE[existing.status] || ""}>{existing.status}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={startEditExisting} data-testid="tt-edit-existing"><Edit className="w-3.5 h-3.5 mr-1" /> Edit fields</Button>
                  <Button type="button" size="sm" onClick={updateExisting} disabled={saving} data-testid="tt-update-existing">{saving ? "Saving…" : "Save changes"}</Button>
                  {isStaff && existing.status !== "approved" && (
                    <Button type="button" size="sm" variant="default" onClick={() => approveEntry(existing.id)} data-testid="tt-approve-existing"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Ready to log — no entry yet for {selectedEmp?.full_name || "this employee"} on {date}.
              </div>
            )}
          </div>

          {/* Row 2: presets row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-muted-foreground mr-1">Presets:</Label>
            <Button type="button" size="sm" variant="outline" onClick={presetStandard} data-testid="tt-preset-9-5">
              {presets.standard.label} · {presets.standard.time_in}–{presets.standard.time_out}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={presetEarly} data-testid="tt-preset-early">
              {presets.early.label} · {presets.early.time_in}–{presets.early.time_out}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={presetLate} data-testid="tt-preset-late">
              {presets.late.label} · {presets.late.time_in}–{presets.late.time_out}
            </Button>
          </div>

          {/* Row 3: times + computed total + save */}
          <form className="grid lg:grid-cols-12 gap-3 items-end" onSubmit={submit}>
            <div className="lg:col-span-2"><Label>Time in</Label><TimeInput value={timeIn} onChange={(e) => setTimeIn(e.target.value)} className="h-11" data-testid="tt-time-in" /></div>
            <div className="lg:col-span-2"><Label>Time out</Label><TimeInput value={timeOut} onChange={(e) => setTimeOut(e.target.value)} className="h-11" data-testid="tt-time-out" /></div>
            <div className="lg:col-span-2"><Label>Break (min)</Label><Input type="number" min="0" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} className="h-11" data-testid="tt-break" /></div>
            <div className="lg:col-span-2">
              <Label>Total</Label>
              <div className="h-11 px-3 rounded-md border border-input bg-muted/40 flex items-center font-mono font-semibold text-base" data-testid="tt-preview-hours">
                {previewHours.toFixed(2)} h
              </div>
            </div>
            <div className="lg:col-span-4">
              <Label>Notes</Label>
              <Input placeholder="Optional" value={notes} onChange={(e) => setNotes(e.target.value)} className="h-11" data-testid="tt-notes" />
            </div>
            <div className="lg:col-span-12 flex justify-end pt-1">
              <Button type="submit" className="h-11 px-6" disabled={saving || !!existing} data-testid="tt-submit">
                <Clock className="w-4 h-4 mr-1.5" />
                {saving ? "Saving…" : existing ? "Already logged — edit above" : "Save entry"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* TODAY'S STATUS GRID */}
      {isStaff && (
        <Card className="border-border shadow-none">
          <div className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="tiny-label">Status grid</div>
              <div className="font-display text-lg font-semibold mt-1">Who has logged on {gridDate}?</div>
              <p className="text-xs text-muted-foreground mt-1">Pending approvals shown first. Click any row to pre-fill the form above.</p>
            </div>
            <div className="flex gap-2 items-end flex-wrap">
              <Input type="date" value={gridDate} onChange={(e) => setGridDate(e.target.value)} className="w-44" data-testid="tt-grid-date" />
              <Button variant={gridDate === todayStr() ? "default" : "outline"} className="h-10 px-3" onClick={() => setGridDate(todayStr())}>Today</Button>
              <Button variant={gridDate === yesterdayStr() ? "default" : "outline"} className="h-10 px-3" onClick={() => setGridDate(yesterdayStr())}>Yesterday</Button>
              <Button variant="outline" className="h-10" onClick={openBulkDialog} data-testid="tt-grid-bulk-log"><Users className="w-4 h-4 mr-1" /> Bulk log</Button>
              {approvableRows.length > 0 && (
                <Button className="h-10" onClick={bulkApproveAllForDay} data-testid="tt-grid-approve-all">
                  <CheckCheck className="w-4 h-4 mr-1" /> Approve all ({approvableRows.length})
                </Button>
              )}
            </div>
          </div>

          {gridSel.size > 0 && (
            <div className="px-5 py-2 border-t border-b border-border bg-muted/40 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm"><strong>{gridSel.size}</strong> selected</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAllApprovable}>Select all pending</Button>
                <Button size="sm" variant="ghost" onClick={clearGridSel}>Clear</Button>
                <Button size="sm" onClick={bulkApproveSelected} data-testid="tt-grid-bulk-approve-selected"><CheckCheck className="w-3.5 h-3.5 mr-1" /> Approve selected</Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto app-scroll">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left tiny-label">
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">In / Out</th>
                  <th className="px-4 py-3 text-right">Hours</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {gridLoading && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>}
                {!gridLoading && gridRows.map((r) => {
                  const canApprove = r.entry && r.entry.status !== "approved";
                  const isSel = r.entry ? gridSel.has(r.entry.id) : false;
                  return (
                  <tr key={r.employee.id} className="border-t border-border hover:bg-muted/30" data-testid={`tt-grid-row-${r.employee.employee_id}`}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      {canApprove && (
                        <Checkbox checked={isSel} onCheckedChange={() => toggleGridSel(r.entry.id)} data-testid={`tt-grid-check-${r.employee.employee_id}`} />
                      )}
                    </td>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => quickFillFromGrid(r)}>
                      <div className="font-medium">{r.employee.full_name}</div>
                      {r.employee.department && (
                        <div className="text-xs text-muted-foreground">{r.employee.department}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize text-xs cursor-pointer" onClick={() => quickFillFromGrid(r)}>{(r.employee.employment_type || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs cursor-pointer" onClick={() => quickFillFromGrid(r)}>{r.entry ? `${r.entry.time_in} – ${r.entry.time_out}` : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-right font-mono cursor-pointer" onClick={() => quickFillFromGrid(r)}>{r.entry ? r.entry.total_hours.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => quickFillFromGrid(r)}>
                      {r.entry ? (
                        <Badge variant="outline" className={STATUS_TONE[r.entry.status] || ""}>{r.entry.status}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">not logged</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canApprove && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); approveEntry(r.entry.id); }} data-testid={`tt-grid-approve-${r.employee.employee_id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {!r.entry && <span className="text-xs text-muted-foreground">click row to log</span>}
                    </td>
                  </tr>
                );})}
                {!gridLoading && gridRows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No active employees.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
